const ONLINE_MS = 2 * 60 * 1000;      // 2 min  → online
const AWAY_MS = 15 * 60 * 1000;       // 15 min → away

/**
 * Compute online status from a last_seen ISO string.
 * Optionally accepts a `createdAt` fallback used when `lastSeenIso` is
 * missing (e.g. users who logged in BEFORE the heartbeat system existed).
 * Returns { status, label, dotClass }.
 */
function statusFromLastSeen(lastSeenIso, createdAtIso) {
  if (!lastSeenIso) {
    if (createdAtIso) {
      try {
        const created = new Date(createdAtIso);
        if (!Number.isNaN(created.getTime())) {
          return {
            status: "no-heartbeat",
            label: `Creato il ${created.toLocaleDateString("it-IT")}`,
            dotClass: "bg-slate-300",
          };
        }
      } catch {
        /* ignore parse errors */
      }
    }
    return {
      status: "never",
      label: "Mai connesso",
      dotClass: "bg-slate-300",
    };
  }
  const last = new Date(lastSeenIso).getTime();
  if (Number.isNaN(last)) {
    return { status: "unknown", label: "—", dotClass: "bg-slate-300" };
  }
  const diff = Date.now() - last;
  if (diff < ONLINE_MS) {
    return {
      status: "online",
      label: "Online ora",
      dotClass: "bg-emerald-500 ring-2 ring-emerald-200",
    };
  }
  if (diff < AWAY_MS) {
    const min = Math.max(1, Math.round(diff / 60000));
    return {
      status: "away",
      label: `Inattivo da ${min} min`,
      dotClass: "bg-amber-400 ring-2 ring-amber-100",
    };
  }
  return {
    status: "offline",
    label: formatLastSeen(diff, lastSeenIso),
    dotClass: "bg-slate-300",
  };
}

function formatLastSeen(diffMs, iso) {
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `Visto ${minutes} min fa`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Visto ${hours} h fa`;
  const days = Math.round(hours / 24);
  if (days < 30) return `Visto ${days} g fa`;
  try {
    return `Visto il ${new Date(iso).toLocaleDateString("it-IT")}`;
  } catch {
    return "Offline";
  }
}

/**
 * Compact online-status indicator: a colored dot + tooltip.
 * Use inline next to a user name in a table.
 */
export default function OnlineDot({ lastSeenAt, createdAt, testid }) {
  const s = statusFromLastSeen(lastSeenAt, createdAt);
  return (
    <span
      className="inline-flex items-center align-middle"
      title={s.label}
      aria-label={s.label}
      data-testid={testid}
      data-status={s.status}
    >
      <span className={`block w-2.5 h-2.5 rounded-full ${s.dotClass}`} />
    </span>
  );
}

export { statusFromLastSeen };
